import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { adminApi } from "@/lib/api";
import { RefreshCw, Eye, RotateCcw, Link2, Send } from "lucide-react";

const ST_CLS = {
  simulated: "bg-amber-50 text-amber-800 border-amber-300",
  queued: "bg-slate-100 text-slate-700 border-slate-300",
  sent: "bg-emerald-50 text-emerald-800 border-emerald-200",
  delivered: "bg-emerald-50 text-emerald-800 border-emerald-200",
  bounced: "bg-rose-50 text-rose-800 border-rose-300",
  failed: "bg-rose-50 text-rose-800 border-rose-300",
  retrying: "bg-amber-50 text-amber-800 border-amber-300",
};

export default function AdminEmailLog() {
  const [status, setStatus] = useState(null);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [testEmail, setTestEmail] = useState("");
  const [testBusy, setTestBusy] = useState(false);

  const sendTest = async () => {
    setTestBusy(true);
    try {
      const r = await adminApi.post("/admin/email-log/test-send", { to_email: testEmail.trim() });
      toast.success(r.data.detail || `Test email: ${r.data.status}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test send failed");
    } finally {
      setTestBusy(false);
    }
  };

  const load = useCallback(() => {
    adminApi.get("/admin/email-log/status").then((r) => setStatus(r.data)).catch(() => toast.error("Could not load email status"));
    const params = {};
    if (filter !== "all") params.status = filter;
    adminApi.get("/admin/email-log", { params }).then((r) => setRows(r.data)).catch(() => {});
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const retry = async (id) => {
    try {
      const r = await adminApi.post(`/admin/email-log/${id}/retry`);
      toast.success(`Retry attempted — status: ${r.data.status}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Retry failed");
    }
  };

  const live = status?.email_mode?.mode === "LIVE";

  return (
    <div className="max-w-6xl">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <h1 className="font-heading font-bold text-2xl text-foreground" data-testid="admin-email-log-title">Email Log / System Status</h1>
          <p className="text-sm text-muted-foreground mt-1">Production delivery log with retry handling. Full message bodies are retained only in simulated mode.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="email-log-refresh"><RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh</Button>
      </div>

      {/* PROMINENT EMAIL MODE BANNER */}
      {status && (
        <div className={`rounded-xl border-2 px-5 py-4 flex items-center gap-4 ${live ? "border-emerald-400 bg-emerald-50" : "border-amber-400 bg-amber-50"}`} data-testid="email-mode-banner">
          <span className={`h-4 w-4 rounded-full ${live ? "bg-emerald-500" : "bg-amber-500"} animate-pulse shrink-0`} />
          <div>
            <p className={`font-heading font-bold text-lg ${live ? "text-emerald-900" : "text-amber-900"}`} data-testid="email-mode-label">
              EMAIL MODE: {status.email_mode.mode}
            </p>
            <p className={`text-sm ${live ? "text-emerald-800" : "text-amber-800"}`}>{status.email_mode.note}</p>
            {status.email_mode.provider && <p className="text-xs text-muted-foreground mt-0.5">Provider: {status.email_mode.provider}</p>}
          </div>
        </div>
      )}

      {/* Live activation gate + test send */}
      {status?.live_activation && (
        <Card className="mt-4" data-testid="live-activation-card">
          <CardContent className="p-5">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="font-heading font-semibold text-sm text-foreground">
                  Production activation
                  {status.live_activation.live_ready
                    ? <Badge variant="outline" className="ml-2 bg-emerald-50 text-emerald-800 border-emerald-200 text-[10px]" data-testid="live-ready-badge">LIVE READY</Badge>
                    : <Badge variant="outline" className="ml-2 bg-amber-50 text-amber-800 border-amber-300 text-[10px]" data-testid="not-live-ready-badge">NOT LIVE</Badge>}
                </p>
                <p className="text-xs text-muted-foreground mt-1 max-w-xl">{status.live_activation.note}</p>
              </div>
              <div className="flex items-end gap-2">
                <div>
                  <p className="text-[10px] text-muted-foreground mb-1">Send a test email</p>
                  <Input className="w-56 h-9" type="email" placeholder="admin@example.com" value={testEmail}
                         onChange={(e) => setTestEmail(e.target.value)} data-testid="test-email-input" />
                </div>
                <Button size="sm" onClick={sendTest} disabled={!testEmail.includes("@") || testBusy} data-testid="test-email-send">
                  <Send className="h-3.5 w-3.5 mr-1.5" /> {testBusy ? "Sending…" : "Send Test Email"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* counts */}
      {!status ? <Skeleton className="h-16 mt-4" /> : (
        <div className="flex flex-wrap gap-2 mt-4" data-testid="email-status-counts">
          {Object.entries(status.counts).map(([k, v]) => (
            <Badge key={k} variant="outline" className={`${ST_CLS[k] || ""} text-xs px-3 py-1`}>{k}: {v}</Badge>
          ))}
          <Badge variant="outline" className="text-xs px-3 py-1">total: {status.total}</Badge>
        </div>
      )}

      <div className="flex items-center gap-3 mt-6 mb-3">
        <p className="text-xs text-muted-foreground">Status filter</p>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-44 bg-white" data-testid="email-log-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All</SelectItem>
            {Object.keys(ST_CLS).map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card><CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Type</TableHead><TableHead>To (masked)</TableHead><TableHead>Status</TableHead>
            <TableHead>Queued</TableHead><TableHead>Retries</TableHead><TableHead>Failure</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody data-testid="email-log-table">
            {rows.map((e) => (
              <TableRow key={e.id}>
                <TableCell className="text-xs font-medium">{e.message_type}{e.is_test && <Badge variant="outline" className="ml-2 bg-amber-50 text-amber-800 border-amber-300 text-[9px]">TEST</Badge>}</TableCell>
                <TableCell className="font-mono text-xs">{e.to_masked}</TableCell>
                <TableCell><Badge variant="outline" className={`${ST_CLS[e.status] || ""} text-[10px]`}>{e.status}</Badge></TableCell>
                <TableCell className="text-xs">{new Date(e.queued_at).toLocaleString("en-CA")}</TableCell>
                <TableCell className="tabular-nums text-sm">{e.retry_count || 0}</TableCell>
                <TableCell className="text-xs max-w-[160px] truncate">{e.failure_reason || "—"}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button size="sm" variant="ghost" data-testid={`email-view-${e.id}`}><Eye className="h-3.5 w-3.5" /></Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle className="font-heading">{e.subject || e.message_type}</DialogTitle>
                          <DialogDescription>To: {e.to_masked} · {e.mode} · {e.status}</DialogDescription>
                        </DialogHeader>
                        {e.preview_link && (
                          <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3" data-testid="preview-verification-link">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-amber-800 mb-1 flex items-center gap-1">
                              <Link2 className="h-3 w-3" /> PREVIEW/TEST VERIFICATION LINK (admin-only)
                            </p>
                            <a href={e.preview_link} className="font-mono text-[11px] text-primary break-all hover:underline">{e.preview_link}</a>
                            <p className="text-[10px] text-amber-800 mt-1">One-time use · expires · never expose publicly.</p>
                          </div>
                        )}
                        {e.body ? (
                          <pre className="text-[11px] bg-slate-50 border border-slate-200 rounded p-3 whitespace-pre-wrap">{e.body}</pre>
                        ) : (
                          <p className="text-xs text-muted-foreground">Message body not retained (live mode metadata-only policy).</p>
                        )}
                      </DialogContent>
                    </Dialog>
                    {["failed", "retrying"].includes(e.status) && (
                      <Button size="sm" variant="ghost" onClick={() => retry(e.id)} data-testid={`email-retry-${e.id}`}><RotateCcw className="h-3.5 w-3.5" /></Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-8">No messages logged yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}
